<section class="px-6 py-20 sm:px-10">
  <article class="mx-auto max-w-3xl">
    <header class="mb-10">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Legal</span
      >
      <h1 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Ghoul Pool Terms of Service
      </h1>
      <p class="mt-3 text-sm text-dusk">Last updated: {{ lastUpdated }}</p>
    </header>

    <section id="who-we-are" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">1. Who we are</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is operated by {{ legalEntity }} ("Ghoulish Mortals", "we", "us") of Saint
        Charles, Illinois. You can reach us at
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a
        >. By creating an account or using the site at {{ prodDomain }}, you agree to these terms.
      </p>
    </section>

    <section id="what-ghoul-pool-is" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">2. What Ghoul Pool is</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is a game. Players pick public figures at the start of a competition and score
        points over the course of it. It is a long-running bit of gallows humor, not a prediction
        market, an insurance product, or a betting platform. Nobody wins anything by causing harm to
        anyone, and using the service to encourage, threaten, or celebrate violence against a real
        person is grounds for immediate termination.
      </p>
    </section>

    <section id="beta" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">3. This is a beta</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is early software offered to a small group of testers. It may break, change
        shape, or lose data. We make no promise of uptime, and we may reset competition data before
        a general release. Do not rely on Ghoul Pool as a record of anything that matters to you.
      </p>
    </section>

    <section id="eligibility" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">4. Eligibility</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        You must be 18 or older to hold an account. One account per person. Give us accurate
        registration details, keep your password to yourself, and tell us at
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a>
        if you believe someone else has gotten into your account. What happens under your account is
        your responsibility.
      </p>
    </section>

    <section id="your-account" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">5. Your account</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        You can close your account at any time by emailing
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a
        >. We can suspend or close an account that breaks these terms, and we will tell you why when
        we do.
      </p>
    </section>

    <section id="acceptable-use" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">6. Acceptable use</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Don't harass other players. Don't add private individuals to the celebrity catalog or pick
        them — Ghoul Pool is about public figures, and entries about private people will be removed.
        Don't scrape the site, probe it for weaknesses, or try to reach data that isn't yours. Don't
        impersonate anyone. Don't use Ghoul Pool for anything illegal where you live.
      </p>
    </section>

    <section id="competitions-prizes-money" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">
        7. Competitions, prizes, and money
      </h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Competition owners set their own rules, deadlines, and prizes, and those arrangements are
        between the owner and their players. We are not a party to them. We do not collect entry
        fees, hold stakes, process payments, verify that a prize exists, or step in when a prize
        goes unpaid. Whether a given pool amounts to regulated gambling where you live is your call
        to make and your responsibility to get right, and running an illegal pool through Ghoul Pool
        is a breach of these terms.
      </p>
    </section>

    <section id="celebrity-information" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">8. Celebrity information</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        The catalog draws on public sources, mainly Wikipedia. It can be incomplete, out of date, or
        wrong, including — bluntly — about whether someone has died. We don't warrant any of it. If
        you spot an error, tell us at
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a>
        and we'll correct it.
      </p>
    </section>

    <section id="scoring-and-administration" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">
        9. Scoring and administration
      </h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Most scoring is automatic and runs on a schedule, so there can be a delay between a
        real-world event and the leaderboard catching up. Some achievements are awarded by a
        competition's owner or cohosts at their discretion, and their decisions are final within
        their own competition. We may recalculate or correct scores when the underlying data turns
        out to be wrong.
      </p>
    </section>

    <section id="paid-tiers" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">10. Paid tiers</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is free today and we collect no payment information. If paid plans launch, we'll
        show you the price and terms before anything is charged. Nothing will start charging you
        automatically.
      </p>
    </section>

    <section id="what-you-post" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">11. What you post</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Competition names, messages to your players, and your display name stay yours. By posting
        them you let us store and display them inside the service so it can work. Keep them civil —
        the acceptable use rules apply to them too.
      </p>
    </section>

    <section id="changes-to-the-service" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">12. Changes to the service</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Features can change or disappear, especially during the beta. We'll try to give notice when
        a change would affect a competition in flight.
      </p>
    </section>

    <section id="no-warranties" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">13. No warranties</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is provided "as is" and "as available", without warranties of any kind, express
        or implied, to the fullest extent Illinois law allows.
      </p>
    </section>

    <section id="limitation-of-liability" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">14. Limitation of liability</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        To the fullest extent permitted by law, neither {{ legalEntity }} nor anyone working on
        Ghoul Pool is liable for indirect, incidental, or consequential damages, or for lost data,
        lost profits, or a prize you didn't receive. Our total liability for any claim is capped at
        what you paid us in the twelve months before it arose — which, on the free tier, is nothing.
      </p>
    </section>

    <section id="indemnity" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">15. Indemnity</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        If your use of Ghoul Pool — a competition you run, a pick you make, something you post —
        leads to a claim against us, you agree to cover the reasonable cost of dealing with it.
      </p>
    </section>

    <section id="changes-to-these-terms" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">16. Changes to these terms</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        We'll post the updated text here and change the date at the top. If a change materially
        affects you, we'll email the address on your account. Using Ghoul Pool after a change means
        you accept it.
      </p>
    </section>

    <section id="governing-law" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">17. Governing law</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        These terms are governed by the laws of the State of Illinois, and any dispute belongs in
        the state or federal courts serving Kane County, Illinois.
      </p>
    </section>

    <section id="contact" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">18. Contact</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a
        >.
      </p>
    </section>

    <p class="mt-8 text-sm text-dusk">
      See also our
      <a routerLink="/privacy" class="text-parchment hover:underline">Privacy Policy</a>.
    </p>
  </article>
</section>
